import { useEffect, useState } from "react";
import { Loader2, CalendarClock, MapPin, Send } from "lucide-react";
import { profSummary } from "../content/professions";
import { SEARCH_T } from "../content/talentI18n";
import { ContactModal, ON_REQ } from "./CandidateSearch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "se_guest_search";
const T = {
  sk: { wait: "Overujeme platbu…", title: "Kandidáti podľa vybraných zručností", until: "Prístup platí do", none: "Žiadny kandidát nespĺňa vybrané zručnosti.", from: "Voľný od", fail: "Platbu sa nepodarilo overiť." },
  en: { wait: "Verifying payment…", title: "Candidates matching the selected skills", until: "Access valid until", none: "No available candidate matches the selected skills.", from: "Available from", fail: "Payment could not be verified." },
  de: { wait: "Zahlung wird geprüft…", title: "Kandidaten nach gewählten Fähigkeiten", until: "Zugang gültig bis", none: "Kein verfügbarer Kandidat erfüllt die gewählten Fähigkeiten.", from: "Verfügbar ab", fail: "Zahlung konnte nicht bestätigt werden." },
  pl: { wait: "Weryfikujemy płatność…", title: "Kandydaci według wybranych umiejętności", until: "Dostęp ważny do", none: "Żaden dostępny kandydat nie spełnia wybranych umiejętności.", from: "Dostępny od", fail: "Nie udało się zweryfikować płatności." },
  fr: { wait: "Vérification du paiement…", title: "Candidats selon les compétences choisies", until: "Accès valable jusqu'au", none: "Aucun candidat disponible ne correspond aux compétences choisies.", from: "Disponible dès", fail: "Le paiement n'a pas pu être vérifié." },
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString("sk-SK") : "");
const UP = {
  sk: [(e) => `Chcete prístup aj nabudúce bez platby navyše? Zaregistrujte sa ako firma s e-mailom ${e} — registrácia je zadarmo.`, "Registrovať firmu zadarmo"],
  en: [(e) => `Want an account for next time? Register as a company with ${e} — registration is free.`, "Register company for free"],
  de: [(e) => `Möchten Sie ein Konto für das nächste Mal? Registrieren Sie sich als Firma mit ${e} — die Registrierung ist kostenlos.`, "Firma kostenlos registrieren"],
  pl: [(e) => `Chcesz mieć konto na przyszłość? Zarejestruj firmę z e-mailem ${e} — rejestracja jest bezpłatna.`, "Zarejestruj firmę za darmo"],
  fr: [(e) => `Un compte pour la prochaine fois ? Inscrivez votre entreprise avec ${e} — l'inscription est gratuite.`, "Inscrire l'entreprise gratuitement"],
};

export const useGuestToken = () => {
  const [token, setToken] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(KEY) || "null"); return s && s.expires_at > new Date().toISOString() ? s.token : ""; } catch { return ""; }
  });
  const [state, setState] = useState("");
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("guest_session");
    if (!sid) return undefined;
    let n = 0; let stop = false; setState("wait");
    const poll = async () => {
      if (stop) return;
      try {
        const d = await (await fetch(`${API}/public/candidates/guest-status/${sid}`)).json();
        if (d.paid && d.token) { localStorage.setItem(KEY, JSON.stringify(d)); setToken(d.token); setState(""); return; }
      } catch { /* retry */ }
      if (++n < 10) setTimeout(poll, 2500); else setState("fail");
    };
    poll();
    return () => { stop = true; };
  }, []);
  return { token, state };
};

export default function GuestResults({ lang, token, state, skills, pf = {}, labels }) {
  const t = T[lang] || T.sk;
  const U = UP[lang] || UP.sk;
  const S = SEARCH_T[lang] || SEARCH_T.sk;
  const [contact, setContact] = useState(null);
  const sendMsg = async (subject, text) => {
    const r = await fetch(`${API}/public/candidates/guest/message`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, contact: contact.contact, subject, text }) });
    if (!r.ok) { const d = await r.json().catch(() => ({})); throw { response: { data: d } }; }
  };
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/public/candidates/guest?token=${token}&skills=${encodeURIComponent(skills.join(","))}&prof=${pf.prof || ""}&level=${pf.level || ""}`)
      .then((r) => (r.ok ? r.json() : null)).then((d) => { if (!d) localStorage.removeItem(KEY); setData(d); }).catch(() => {});
  }, [token, skills, pf]);
  if (state === "wait") return <p className="mt-6 text-[#D4AF37] text-sm flex items-center gap-2" data-testid="guest-wait"><Loader2 size={14} className="animate-spin" /> {t.wait}</p>;
  if (state === "fail") return <p className="mt-6 text-red-300 text-sm" data-testid="guest-fail">{t.fail}</p>;
  if (!data) return null;
  return (
    <div className="mt-8" data-testid="guest-results">
      <div className="flex flex-wrap justify-between gap-2 mb-4">
        <p className="text-white font-semibold">{t.title} ({data.results.length})</p>
        <p className="text-white/40 text-xs">{t.until} {fmt(data.expires_at)}</p>
      </div>
      {data.results.length === 0 ? <p className="text-white/45 text-sm">{t.none}</p> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {data.results.map((c) => (
            <div key={c.certificate_no} className="border border-white/10 bg-[#0d0d0c] p-4" data-testid={`guest-cand-${c.certificate_no}`}>
              <p className="font-display text-lg text-white">{c.name}</p>
              <p className="text-[#D4AF37] text-xs font-mono">{c.certificate_no}</p>
              <p className="text-white/55 text-xs mt-2 flex items-center gap-3">
                <span className={`inline-flex items-center gap-1 ${c.available ? "text-emerald-300" : ""}`}><CalendarClock size={12} /> {c.available ? `${t.from} ${fmt(c.available_from)}` : (ON_REQ[lang] || ON_REQ.sk)}</span>
                {c.city && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {c.city}</span>}
              </p>
              {profSummary(c, lang) && <p className="text-[#F2E3C6] text-xs mt-2">{profSummary(c, lang)}</p>}
              <p className="text-white/40 text-xs mt-2">{(c.skills || []).filter((s) => labels[s]).map((s) => labels[s]).join(", ")}</p>
              <button onClick={() => setContact(c)} data-testid={`guest-contact-${c.certificate_no}`}
                className="mt-3 inline-flex items-center gap-1.5 border border-[#D4AF37] text-[#D4AF37] px-4 py-1.5 text-sm hover:bg-[#D4AF37] hover:text-black transition-colors"><Send size={13} /> {S.contact}</button>
            </div>
          ))}
        </div>
      )}
      {contact && <ContactModal S={S} cand={contact} onSend={sendMsg} onClose={() => setContact(null)} lang={lang} />}
      <div className="mt-6 border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="guest-upgrade">
        <p className="text-white/75 text-sm max-w-xl">{U[0](data.email)}</p>
        <a href="/ziadost" data-testid="guest-upgrade-btn" className="bg-[#D4AF37] text-black text-sm font-semibold px-5 py-2.5 hover:bg-[#F2E3C6] transition-colors">{U[1]}</a>
      </div>
    </div>
  );
}
